import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { AiEmailUpdateRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useSubscribeAiEmailUpdate = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: AiEmailUpdateRequest) => api.postAiEmailUpdateV1(data),
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("component.ask-nobi-modal.get-updates.error"),
      });
    },
  });
};

const useUnsubscribeAiEmailUpdate = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => api.deleteAiEmailUpdateByIdV1(id),
    onSuccess() {
      showFlashToast({ variant: "success", message: t("page.ai-email-update.unsubscribe.success") });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.ai-email-update.unsubscribe.error") });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AI_EMAIL_UPDATES(projectId) });
    },
  });
};

export const aiEmailUpdateMutations = { useSubscribeAiEmailUpdate, useUnsubscribeAiEmailUpdate };
